interface IAllmylesIconProps {
  className?: string;
  // `light` is for dark backgrounds: white wordmark, and the mark without the
  // multiply blend (which would darken it into the background).
  variant?: 'dark' | 'light';
}

const AllmylesLogo: React.FC<IAllmylesIconProps> = ({
  className,
  variant = 'dark',
}) => {
  // Presentation attributes rather than a <style> block: SVG <style> rules
  // are global, so a light logo in the footer would recolour the dark one in
  // the navbar. Gradient ids are per variant for the same reason.
  const wordmark = variant === 'light' ? '#ffffff' : '#231f20';
  const blend = variant === 'light' ? undefined : ('multiply' as const);
  const gradient1 = `allmyles-logo-gradient-1-${variant}`;
  const gradient2 = `allmyles-logo-gradient-2-${variant}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width="170.92"
      height="40.09"
      viewBox="0 0 516.92 100.09"
      className={className}
    >
      <defs>
        <linearGradient
          id={gradient1}
          y1="50.05"
          x2="90.81"
          y2="50.05"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#babdff" stopOpacity="0.4" />
          <stop offset="1" stopColor="#7d82ff" />
        </linearGradient>
        <linearGradient
          id={gradient2}
          x1="24.74"
          y1="50.05"
          x2="116.69"
          y2="50.05"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#b5c9ea" stopOpacity="0.5" />
          <stop offset="0.11" stopColor="#9db0eb" stopOpacity="0.61" />
          <stop offset="0.28" stopColor="#7f91ec" stopOpacity="0.75" />
          <stop offset="0.45" stopColor="#6879ec" stopOpacity="0.86" />
          <stop offset="0.62" stopColor="#5868ed" stopOpacity="0.94" />
          <stop offset="0.8" stopColor="#4e5ded" stopOpacity="0.98" />
          <stop offset="1" stopColor="#4b5aed" />
        </linearGradient>
      </defs>
      <title>allmyles logo</title>
      <g style={{ isolation: 'isolate' }}>
        <g id="Layer_1" data-name="Layer 1">
          <path
            fill={wordmark}
            d="M129.08,78.46l22.28-62.38a2,2,0,0,1,1.88-1.33h18.23a2,2,0,0,1,1.89,1.33l22.1,62.38a2,2,0,0,1-1.89,2.67h-14.5a2,2,0,0,1-1.9-1.37l-3.27-9.87a2,2,0,0,0-1.9-1.37H151.77a2,2,0,0,0-1.9,1.38l-3.18,9.84a2,2,0,0,1-1.9,1.38H131A2,2,0,0,1,129.08,78.46Zm27-23.69h11.47a1,1,0,0,0,1-1.28L162.27,32a.5.5,0,0,0-1,0l-6.17,21.51A1,1,0,0,0,156.1,54.76Z"
          />
          <path
            fill={wordmark}
            d="M202.67,14.75h12.41a2,2,0,0,1,2,2V79.13a2,2,0,0,1-2,2H202.67a2,2,0,0,1-2-2V16.75A2,2,0,0,1,202.67,14.75Z"
          />
          <path
            fill={wordmark}
            d="M227.6,14.75H240a2,2,0,0,1,2,2V79.13a2,2,0,0,1-2,2H227.6a2,2,0,0,1-2-2V16.75A2,2,0,0,1,227.6,14.75Z"
          />
          <path
            fill={wordmark}
            d="M324.27,36.89q4.88,5.26,4.88,18.06V79.22a2,2,0,0,1-2,2H314.75a2,2,0,0,1-2-2V54q0-4.64-1.61-7t-5.22-2.32a7.3,7.3,0,0,0-5.69,2.42q-2.18,2.42-2.18,6.87V79.22a2,2,0,0,1-2,2H283.65a2,2,0,0,1-2-2V54q0-4.74-1.57-7t-5.26-2.27a7.27,7.27,0,0,0-5.74,2.42q-2.13,2.42-2.13,6.87V79.22a2,2,0,0,1-2,2H252.54a2,2,0,0,1-2-2v-45a2,2,0,0,1,2-2h10.79a2,2,0,0,1,2,1.59l.9,4.29a17,17,0,0,1,6-4.74,21.24,21.24,0,0,1,8.58-1.71q10.15,0,14.22,7.78a17.77,17.77,0,0,1,7.07-5.78,22.71,22.71,0,0,1,9.63-2A16.38,16.38,0,0,1,324.27,36.89Z"
          />
          <path
            fill={wordmark}
            d="M382.57,32.2H369.41a2,2,0,0,0-1.91,1.4l-9.23,29.6a.5.5,0,0,1-1,0L348,33.6a2,2,0,0,0-1.91-1.4H333a2,2,0,0,0-1.87,2.71l11.22,29.41v0l.87,2.24h0l6.9,17.75a1.6,1.6,0,0,1-1.5,2.19H337.32a2,2,0,0,0-2,2v9.56a2,2,0,0,0,2,2h13a12.85,12.85,0,0,0,6.4-1.42,11.67,11.67,0,0,0,4.17-4.07,41.65,41.65,0,0,0,3.37-7.21l20.2-52.48A2,2,0,0,0,382.57,32.2Z"
          />
          <path
            fill={wordmark}
            d="M391.94,14.75h12.41a2,2,0,0,1,2,2V79.13a2,2,0,0,1-2,2H391.94a2,2,0,0,1-2-2V16.75A2,2,0,0,1,391.94,14.75Z"
          />
          <path
            fill={wordmark}
            d="M416.07,43.48A21.84,21.84,0,0,1,425,34.71a27.72,27.72,0,0,1,13.32-3.08A30.48,30.48,0,0,1,452,34.57a22.26,22.26,0,0,1,9.25,8.3,23,23,0,0,1,3.32,12.38,30.36,30.36,0,0,1-.19,3.58,2,2,0,0,1-2,1.73H430v.19q1.23,9.1,9.48,9.1,6.16,0,8-4.17a2,2,0,0,1,1.84-1.23h12.24A2,2,0,0,1,463.45,67a18.61,18.61,0,0,1-3.1,6.08,20.69,20.69,0,0,1-8.06,6.26,27.87,27.87,0,0,1-11.71,2.32,33.8,33.8,0,0,1-14.89-3A21.63,21.63,0,0,1,416.16,70a25.63,25.63,0,0,1-3.27-13.18A27,27,0,0,1,416.07,43.48Zm32.29,7a8.42,8.42,0,0,0-3.08-5.36,9.78,9.78,0,0,0-6.12-1.85A9.53,9.53,0,0,0,433,45.14a8.21,8.21,0,0,0-2.89,5.36Z"
          />
          <path
            fill={wordmark}
            d="M482.7,64.72A1.92,1.92,0,0,1,484.46,66,6.5,6.5,0,0,0,487,69a10.61,10.61,0,0,0,6,1.61,13.43,13.43,0,0,0,5.88-1A3.41,3.41,0,0,0,501,66.34a2.32,2.32,0,0,0-.9-2,6.87,6.87,0,0,0-2.84-1q-1.94-.33-6.78-1Q480.31,61.12,475,58.09t-5.26-10a14.46,14.46,0,0,1,6-12.14q6-4.55,16.07-4.55,10.72,0,17.35,4.31A14.43,14.43,0,0,1,515.94,45a2,2,0,0,1-2,2.43H502.43a2,2,0,0,1-1.83-1.2,5,5,0,0,0-2.18-2.35,11.26,11.26,0,0,0-5.88-1.38,10.9,10.9,0,0,0-5.22,1,3.3,3.3,0,0,0-1.9,3,2.3,2.3,0,0,0,1.9,2.23,37,37,0,0,0,6.92,1.09l1.9.19q9.86,1,15.31,4.22t5.45,10.86A13.61,13.61,0,0,1,514,73.92a18.89,18.89,0,0,1-8.3,5.74,35,35,0,0,1-12.47,2q-10.62,0-17.26-4.6a15.57,15.57,0,0,1-6.85-10.23,1.85,1.85,0,0,1,1.84-2.14Z"
          />
          <g>
            <path
              fill={`url(#${gradient1})`}
              style={{ mixBlendMode: blend }}
              d="M88.08,57.25,55.51,94a10.91,10.91,0,0,1-8.15,3.67H10.9A10.87,10.87,0,0,1,1.66,81.1L17.47,55.68a10.83,10.83,0,0,0,0-11.5L1.73,19A10.86,10.86,0,0,1,11,2.39H47.37a10.86,10.86,0,0,1,8.15,3.67L88.08,42.84A10.87,10.87,0,0,1,88.08,57.25Z"
            />
            <path
              fill={`url(#${gradient2})`}
              style={{ mixBlendMode: blend }}
              d="M113.95,57.25,81.38,94a10.86,10.86,0,0,1-8.13,3.67H35.62A10.87,10.87,0,0,1,26.4,81.1L42.21,55.68a10.89,10.89,0,0,0,0-11.5L26.47,19A10.86,10.86,0,0,1,35.68,2.39H73.24a10.8,10.8,0,0,1,8.13,3.67l32.57,36.79A10.83,10.83,0,0,1,113.95,57.25Z"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export default AllmylesLogo;
